import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import arcValveImage from '../../../images/herosection/valveservices.jpg';
import arcValve1 from '../../../images/ControlArcValve/ARCValve1.png';
import arcValve2 from '../../../images/ControlArcValve/ARCValve2.png';
import EnquiryForm from '../../../components/EnquiryForm/EnquiryForm';
import './ArcValveService.css';

const ArcValveService = () => {
  const features = [
    'All brands and sizes supported',
    'Pressure class 600#, 900#, 1500#',
    'ARC valve diagnostics and testing',
    'Component replacement and refurbishment',
    'Performance optimization',
    'Emergency repair services',
    'Workshop and on-site support',
    'Spare support',
    'Reconditioning/refurbishment work',
    'Bypass assembly',
    'Control head'
  ];

  return (
    <div className="arc-valve-service-page">
      {/* Hero Section */}
      <section className="arc-valve-hero" style={{ backgroundImage: `url(${arcValveImage})` }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="arc-valve-hero-content"
          >
            <Breadcrumb />
            <h1 className="arc-valve-hero-title">
              ARC <span className="arc-valve-hero-title-highlight">Valve Service</span>
            </h1>
            <p className="arc-valve-hero-description">
              Specialized ARC valve support, maintenance and refurbishment. 
              Expert diagnostics, repair and performance optimization for all makes and models.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="arc-valve-overview">
        <div className="container-custom">
          <div className="arc-valve-overview-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="arc-valve-overview-content"
            >
              <h2 className="arc-valve-overview-title">Expert ARC Valve Solutions</h2>
              <p className="arc-valve-overview-description">
                We provide comprehensive ARC valve services including diagnostics, repair, refurbishment and performance optimization. 
                Our team has the expertise and specifications to service all makes and models with workshop and on-site support.
              </p>
              <div className="arc-valve-features">
                {features.map((feature, index) => (
                  <div key={index} className="arc-valve-feature-item">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="arc-valve-overview-visual"
            >
              <div className="arc-valve-image-gallery">
                {[
                  { src: arcValve1, alt: 'ARC valve service', title: 'Diagnostics' },
                  { src: arcValve2, alt: 'ARC valve repair', title: 'Repair & Refurbishment' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="arc-valve-image-card"
                  >
                    <div className="arc-valve-image-card-inner">
                      <img src={item.src} alt={item.alt} />
                      <div className="arc-valve-image-card-overlay">
                        <h4>{item.title}</h4>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Generic Brands Section */}
      <section className="arc-valve-brands">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="arc-valve-brands-header"
          >
            <h2 className="arc-valve-brands-title">We Work With All Brands</h2>
            <p className="arc-valve-brands-description">
              We service all major ARC valve manufacturers. Our team has the expertise and specifications to repair and maintain equipment from any brand.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="arc-valve-cta">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="arc-valve-cta-content"
          >
            <h2 className="arc-valve-cta-title">Need ARC Valve Service?</h2>
            <p className="arc-valve-cta-description">
              Get a free consultation and quote by filling out the form below
            </p>
            <div className="arc-valve-cta-form">
              <EnquiryForm serviceName="ARC Valve" />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ArcValveService;
